import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import { designSystem } from '@yola/ws-ui';

const { Heading, SearchableSelectField } = designSystem;

const SearchableSelectControl = ({
  title,
  short = false,
  control,
  name,
  rules = {},
  options,
  components = null,
  noResultsText = '',
  modalContentRef,
  contentRef,
  isAdvancedFontCustomizationAvailable,
  onChange,
  onFocus,
  ...otherProps
}) => {
  const selectWrapperRef = useRef(null);
  const selectRef = useRef(null);
  const [menuPlacement, setMenuPlacement] = useState('auto');
  const isMobile = bowser.mobile;

  const classes = classNames('ws-design-fonts-dialog__select', {
    'ws-design-fonts-dialog__select--short': short,
  });

  const handleFocus = () => {
    onFocus();

    if (isMobile) {
      selectRef?.current?.openMenuAutocomplete();
    }
  };

  useEffect(() => {
    const contentContainer = contentRef.current;

    const handleClick = () => {
      const modalBottom = modalContentRef.current?.getBoundingClientRect().bottom;
      const selectBottom = selectWrapperRef.current?.getBoundingClientRect().bottom;

      if (modalBottom - selectBottom < 48) {
        setMenuPlacement('top');
      } else {
        setMenuPlacement('auto');
      }
    };

    contentContainer?.addEventListener('scroll', handleClick);

    return () => contentContainer?.removeEventListener('scroll', handleClick);

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="ws-design-fonts-dialog__control">
      <div className="ws-design-fonts-dialog__control--title">
        <Heading type="heading-6" singleLine>
          {title}
        </Heading>
      </div>
      <div className={classes} ref={selectWrapperRef}>
        <SearchableSelectField
          control={control}
          name={name}
          rules={rules}
          noResultsText={noResultsText}
          preventOverflow
          {...(isAdvancedFontCustomizationAvailable && { openMenuOnFocus: true })}
          options={options}
          components={components}
          menuPlacement={menuPlacement}
          onChange={onChange}
          onFocus={handleFocus}
          {...otherProps}
        />
      </div>
    </div>
  );
};

SearchableSelectControl.propTypes = {
  title: PropTypes.node.isRequired,
  short: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  name: PropTypes.string.isRequired,
  rules: PropTypes.shape({}),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  components: PropTypes.oneOfType(
    PropTypes.shape({
      Option: PropTypes.func,
      SingleValue: PropTypes.func,
      Placeholder: PropTypes.func,
      Input: PropTypes.func,
      ValueContainer: PropTypes.func,
    }),
    PropTypes.oneOf([null])
  ),
  noResultsText: PropTypes.string,
  modalContentRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  contentRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  isAdvancedFontCustomizationAvailable: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};

export default SearchableSelectControl;
